import { type Id, toast as rtToast, type ToastOptions } from 'react-toastify';
import { ChakraToast } from './ChakraToast';

interface ToastConfig extends Omit<ToastOptions, 'type'> {
  description?: string;
}

// react-toastify v11 bug: closeToast can throw if the toast entry is already
// removed from the internal store (e.g. race between autoClose and manual dismiss).
function safeClose(fn?: () => void): (() => void) | undefined {
  if (!fn) {
    return undefined;
  }
  return () => {
    try {
      fn();
    } catch {
      // swallow — toast was already removed
    }
  };
}

export const toast = {
  success(title: string, config?: ToastConfig): Id {
    return rtToast(
      ({ closeToast }) =>
        ChakraToast({
          title,
          description: config?.description,
          status: 'success',
          closeToast: safeClose(closeToast),
        }),
      { autoClose: 3000, ...config }
    );
  },
  error(title: string, config?: ToastConfig): Id {
    return rtToast(
      ({ closeToast }) =>
        ChakraToast({
          title,
          description: config?.description,
          status: 'error',
          closeToast: safeClose(closeToast),
        }),
      { autoClose: false, ...config }
    );
  },
  warning(title: string, config?: ToastConfig): Id {
    return rtToast(
      ({ closeToast }) =>
        ChakraToast({
          title,
          description: config?.description,
          status: 'warning',
          closeToast: safeClose(closeToast),
        }),
      config
    );
  },
  info(title: string, config?: ToastConfig): Id {
    return rtToast(
      ({ closeToast }) =>
        ChakraToast({
          title,
          description: config?.description,
          status: 'info',
          closeToast: safeClose(closeToast),
        }),
      config
    );
  },
  loading(title: string, config?: ToastConfig): Id {
    return rtToast(
      ({ closeToast }) =>
        ChakraToast({
          title,
          description: config?.description,
          status: 'loading',
          closeToast: safeClose(closeToast),
        }),
      { autoClose: false, isLoading: true, ...config }
    );
  },
  dismiss(id?: Id): void {
    try {
      rtToast.dismiss(id);
    } catch {
      // react-toastify v11 bug: crashes if toast entry is already removed from store
    }
  },
  promise: rtToast.promise,
} as const;
